<script lang="ts" setup>
import { ref, computed, defineModel } from "vue";
import VModal from "@magnit/core/src/components/VModal/VModal.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import IconBack from "@magnit/icons/src/assets/icons/arrow-left-corner-24.svg";
import VModalAuthPhoneForm from "./components/VModalAuthPhoneForm.vue";
import VModalAuthCodeForm from "./components/VModalAuthCodeForm.vue";
import type { IModalAuthProps, TModalAuthForm } from "./VModalAuth.types";
import type { IModalProps } from "@magnit/core/src/components/VModal/VModal.types";
import { useAnalyticManager } from "@magnit/analytic-events";
import { formatPhoneNumber } from "@magnit/core/src/utilities/format";
import {
  LABEL_PHONE_TITLE,
  LABEL_CODE_TITLE,
  LABEL_PHONE_SUBTITLE,
  LABEL_CODE_SUBTITLE,
  ELEMENT_VMODAL_ID,
  EVENT_AUTH_CLOSE,
  EVENT_OTP_BACK,
} from "./constants";

const props = withDefaults(defineProps<IModalAuthProps>(), {
  form: "phone",
  codeLength: 5,
  pending: false,
  error: false,
  errorText: "",
});
const emit = defineEmits<{
  close: [];
  "submit:phone": [{ phone: string; captchaToken: string }];
  "submit:code": [{ code: string }];
}>();
const { send } = useAnalyticManager();

const formModel = defineModel<TModalAuthForm>("form");
const captchaToken = ref<string>("");

const formatPhone = () => {
  if (!props.phone) return "";
  return formatPhoneNumber(`7${props.phone}`);
};
const modalProps = computed(
  () =>
    ({
      id: ELEMENT_VMODAL_ID,
      title: formModel.value === "phone" ? LABEL_PHONE_TITLE : LABEL_CODE_TITLE,
      subtitle:
        formModel.value === "phone"
          ? LABEL_PHONE_SUBTITLE
          : `${LABEL_CODE_SUBTITLE} ${formatPhone()}`,
      open: true,
      active: true,
      type: { xs: "bottom-sheet", m: "modal" },
      ...(props.modal ?? {}),
    }) as IModalProps,
);
const phoneForm = computed(() => formModel.value === "phone");
const codeForm = computed(() => formModel.value === "code");

const onAnalytics = (payload: {
  eventName: string;
  params?: Record<string, unknown>;
}) => send(payload.eventName, payload.params);
const onModalClose = () => {
  onAnalytics({ eventName: EVENT_AUTH_CLOSE });
  emit("close");
};
const onBackClick = () => {
  if (phoneForm.value) return;
  formModel.value = "phone";
  onAnalytics({ eventName: EVENT_OTP_BACK });
};
const onResend = () => onPhoneSubmit(props.phone as string);
const onPhoneSubmit = (phone: string) => {
  if (props.pending || props.success || !phone) return;
  emit("submit:phone", { phone, captchaToken: captchaToken.value });
};
const onCodeSubmit = (code: string) => {
  if (props.pending || props.success || !code) return;
  emit("submit:code", { code });
};
</script>

<template>
  <VModal v-bind="modalProps" @header-left="onBackClick" @close="onModalClose">
    <template #headerLeft>
      <VHidden :hide="phoneForm">
        <IconBack />
      </VHidden>
    </template>
    <VModalAuthPhoneForm
      v-if="phoneForm"
      v-model:captchaToken="captchaToken"
      :phone="phone"
      :captcha-key="captchaKey"
      :pending="pending"
      :error="error"
      :error-text="errorText"
      :invisible-captcha="invisibleCaptcha"
      :disable-captcha="disableCaptcha"
      @analytics="onAnalytics"
      @submit="onPhoneSubmit"
    />
    <VModalAuthCodeForm
      v-if="codeForm"
      v-model:captchaToken="captchaToken"
      :captcha-key="captchaKey"
      :pending="pending"
      :error="error"
      :success="success"
      :error-text="errorText"
      :code-length="codeLength"
      :expires="expires"
      :invisible-captcha="invisibleCaptcha"
      :disable-captcha="disableCaptcha"
      @analytics="onAnalytics"
      @submit="onCodeSubmit"
      @resend="onResend"
    />
    <slot name="disclaimer" />
  </VModal>
</template>
